import React from 'react';
import { Space, Table, Tag, Tooltip, Typography, theme } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  Dash,
  FailureCallout,
  STEP_DESCRIPTIONS,
  StepResult,
  StepResultIcon,
  TargetResult,
  firstFailedStep,
  isSkippedStep,
  stepLabel,
} from './statusVisuals';

const { Text } = Typography;
const { useToken } = theme;

// The cascade a check ran, and — one level deeper — the target URLs a step
// tried. Both live inside an expanded row, so every column here is sized for
// the ~960px that is left of a 1280px screen after the table's own indent.
const TargetsTable: React.FC<{ targets: TargetResult[] }> = ({ targets }) => {
  const { token } = useToken();
  return (
    <Table<TargetResult>
      dataSource={targets}
      rowKey="url"
      size="small"
      pagination={false}
      columns={[
        {
          title: 'Target URL',
          dataIndex: 'url',
          key: 'url',
          ellipsis: true,
          render: (url: string) => (
            <Tooltip title={url} placement="topLeft"><Text style={{ fontSize: 12 }}>{url}</Text></Tooltip>
          ),
        },
        {
          title: 'Result',
          dataIndex: 'success',
          key: 'success',
          width: 68,
          // Skipped is a third outcome, not a shade of failure: this proxy was
          // never asked to reach that target.
          render: (success: boolean, record: TargetResult) => record.skipped
            ? <Tag style={{ margin: 0, fontSize: 12 }}>skipped</Tag>
            : success
              ? <CheckCircleOutlined style={{ color: token.colorSuccess }} />
              : <CloseCircleOutlined style={{ color: token.colorError }} />,
        },
        {
          title: 'Content',
          key: 'content',
          width: 104,
          render: (_: unknown, record: TargetResult) => {
            if (record.contentValidated) return <Tag color="green" style={{ margin: 0, fontSize: 12 }}>Validated</Tag>;
            if (record.contentMismatch) return <Tag color="red" style={{ margin: 0, fontSize: 12 }}>Mismatch</Tag>;
            return <Tag style={{ margin: 0, fontSize: 12 }}>Not checked</Tag>;
          },
        },
        {
          title: 'Latency',
          dataIndex: 'latency',
          key: 'latency',
          width: 84,
          align: 'right' as const,
          render: (v: number) => v > 0 ? `${v.toFixed(1)} ms` : <Dash />,
        },
        {
          title: 'Error',
          dataIndex: 'error',
          key: 'error',
          // Not ellipsised: the per-target error is the finding on a
          // proxy_request failure ("content_mismatch: expected content not
          // found in response body" says something a clipped version does not).
          render: (err: string, record: TargetResult) => err
            ? (
              <Text
                type={record.skipped ? 'secondary' : 'danger'}
                style={{ fontSize: 12, wordBreak: 'break-word' }}
              >
                {err}
              </Text>
            )
            : <Dash />,
        },
      ]}
    />
  );
};

const StepsTable: React.FC<{ steps: StepResult[] }> = ({ steps }) => {
  const { token } = useToken();
  const broke = firstFailedStep(steps);
  // The targets of the step that failed are the diagnosis, not a footnote:
  // they open with the row instead of hiding behind a second expander.
  const openByDefault = (steps || [])
    .filter((s) => s.targetResults && s.targetResults.length > 0 && (!s.success || s === broke))
    .map((s) => s.name);

  return (
    <>
      <FailureCallout steps={steps} />
      <Table<StepResult>
        dataSource={steps}
        rowKey="name"
        size="small"
        pagination={false}
        // The failing row is tinted as well as marked, so the eye lands on it
        // before it starts reading step names.
        onRow={(step) => ({
          style: step === broke ? { background: token.colorErrorBg } : undefined,
        })}
        columns={[
          {
            title: 'Step',
            dataIndex: 'name',
            key: 'name',
            width: 186,
            render: (name: string, record: StepResult) => (
              <Space size={4}>
                <Text
                  strong={record === broke}
                  type={isSkippedStep(record) ? 'secondary' : undefined}
                  style={{ fontSize: 13 }}
                >
                  {stepLabel(name)}
                </Text>
                {STEP_DESCRIPTIONS[name] && (
                  <Tooltip title={STEP_DESCRIPTIONS[name]}>
                    <InfoCircleOutlined style={{ color: token.colorTextTertiary, fontSize: 12, cursor: 'help' }} />
                  </Tooltip>
                )}
              </Space>
            ),
          },
          {
            title: 'Result',
            key: 'success',
            width: 62,
            render: (_: unknown, record: StepResult) => <StepResultIcon step={record} />,
          },
          {
            title: 'Latency',
            dataIndex: 'latency',
            key: 'latency',
            width: 78,
            align: 'right' as const,
            render: (v: number) => v > 0 ? `${v.toFixed(1)} ms` : <Dash />,
          },
          {
            title: 'Error',
            dataIndex: 'error',
            key: 'error',
            // Wraps rather than ellipsises. This column is the reason the page
            // was opened; a tooltip is not good enough for the one string the
            // operator has to read, copy and act on.
            render: (err: string, record: StepResult) => {
              if (!err) return <Dash />;
              const skipped = isSkippedStep(record);
              return (
                <Text
                  type={skipped ? 'secondary' : 'danger'}
                  style={{ fontSize: 12, wordBreak: 'break-word' }}
                >
                  {skipped ? 'not attempted — ' : ''}
                  {err.replace(/^skipped:\s*/i, '')}
                </Text>
              );
            },
          },
          {
            title: 'Note',
            key: 'notes',
            width: 178,
            render: (_: unknown, record: StepResult) => {
              const notes = record.notes || [];
              if (notes.length === 0) return <Dash />;
              const color = record.severity === 'warn'
                ? token.colorWarning
                : record.severity === 'info'
                  ? token.colorInfo
                  : token.colorTextSecondary;
              return (
                <Tooltip title={notes.join(' · ')}>
                  <Space direction="vertical" size={0}>
                    {notes.map((n, i) => (
                      <Text key={i} style={{ color, fontSize: 12 }} ellipsis>{n}</Text>
                    ))}
                  </Space>
                </Tooltip>
              );
            },
          },
        ]}
        expandable={{
          defaultExpandedRowKeys: openByDefault,
          expandedRowRender: (step: StepResult) =>
            step.targetResults && step.targetResults.length > 0
              ? <TargetsTable targets={step.targetResults} />
              : null,
          rowExpandable: (step: StepResult) => !!(step.targetResults && step.targetResults.length > 0),
        }}
      />
    </>
  );
};

export default StepsTable;
